import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, ChevronRight, Ellipsis, Info, Layers, List } from 'lucide-react';

import { useCreditMutations } from '../../../hooks/useApi';
import { cn } from '../../../lib/utils';
import type { LedgerEntrySummary } from '../../../services/api';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { ConfirmDialog } from '../../ui/confirm-dialog';
import { InlineAlert } from '../../ui/inline-alert';
import { LoadingState } from '../../LoadingSpinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { BreakableEmail } from '../../ui/breakable-email';
import {
    menuItemClassName,
    menuItemDestructiveClassName,
    menuSeparatorClassName,
    popoverSurfaceClassName,
} from '../../ui/surface';
import { SegmentedControl } from '../../ui/segmented-control';
import { overlineClassName } from '../../ui/typography';
import { useMenuKeyboard } from '../../ui/use-menu-keyboard';
import {
    adminCreditBadgeClassName,
    canAdjustAdminLedgerRow,
    canCancelPendingCreditReservation,
    canRefundAdminLedgerRow,
    canReassignAdminLedgerRow,
    pickGroupedRowActionEntry,
    formatLedgerUsageAmount,
    formatLinkedArtifactsTypeLabel,
    getLedgerTypeDisplayLabel,
    getLedgerUsageAmount,
    getPoolLabel,
    ledgerPoolBadgeClass,
} from './adminCreditAdapters';
import { CreditCopyButton } from './CreditCopyButton';
import { LedgerEntryDetailsDialog } from './ledgerDialogs';
import {
    applyReservationAwareLedgerStates,
    chronologicallyFirstLedgerEntry,
    compositeLedgerState,
    filterLedgerEntriesExcludingPoolTypes,
    groupLedgerByLinkedOrBatch,
    groupLatestTimestamp,
    ledgerEntrySectionKey,
    ledgerEntriesNewestFirst,
    sortPoolTypesForDisplay,
    type SowLedgerTableViewMode,
} from './sowLedgerTableGrouping';

function groupedLedgerUsageLabel(section: string): 'Reserved' | 'Spent' {
    return section === 'pending' ? 'Reserved' : 'Spent';
}

type SowLedgerTableSectionProps = {
    ledgerEntries: LedgerEntrySummary[];
    poolTypes?: string[];
    labelledBy?: string;
    onAdjustRow: (entry: LedgerEntrySummary) => void;
    onRefundRow: (entry: LedgerEntrySummary) => void;
    onReassignRow: (entry: LedgerEntrySummary) => void;
    isLoading?: boolean;
    isError?: boolean;
    onRetry?: () => void;
    isRetrying?: boolean;
};

export function SowLedgerTableSection({
    ledgerEntries,
    poolTypes,
    labelledBy,
    onAdjustRow,
    onRefundRow,
    onReassignRow,
    isLoading = false,
    isError = false,
    onRetry,
    isRetrying = false,
}: SowLedgerTableSectionProps) {
    const { cancelCreditReservation, commitCreditReservation } = useCreditMutations();
    const [viewMode, setViewMode] = useState<SowLedgerTableViewMode>('grouped');
    const [expandedGroupKeys, setExpandedGroupKeys] = useState<Record<string, boolean>>({});
    const [excludedPoolTypes, setExcludedPoolTypes] = useState<Set<string>>(() => new Set());
    const [cancellingReservationId, setCancellingReservationId] = useState<string | null>(null);
    const [committingReservationId, setCommittingReservationId] = useState<string | null>(null);
    const [detailsEntry, setDetailsEntry] = useState<LedgerEntrySummary | null>(null);
    const [reservationConfirm, setReservationConfirm] = useState<{
        kind: 'commit' | 'cancel';
        entry: LedgerEntrySummary;
    } | null>(null);
    const [reservationError, setReservationError] = useState<string | null>(null);
    const tableColSpan = 9;

    const availablePoolTypes = useMemo(() => {
        const types = new Set<string>(poolTypes ?? []);
        for (const entry of ledgerEntries) {
            types.add(entry.poolType);
        }
        return sortPoolTypesForDisplay(types);
    }, [ledgerEntries, poolTypes]);
    const availablePoolTypesKey = availablePoolTypes.join('|');

    useEffect(() => {
        setExcludedPoolTypes(new Set());
    }, [availablePoolTypesKey]);

    useEffect(() => {
        setExpandedGroupKeys({});
    }, [viewMode, excludedPoolTypes]);

    const reservationAwareLedgerEntries = useMemo(
        () => applyReservationAwareLedgerStates(ledgerEntries),
        [ledgerEntries],
    );

    const filteredLedgerEntries = useMemo(
        () =>
            filterLedgerEntriesExcludingPoolTypes(reservationAwareLedgerEntries, excludedPoolTypes),
        [excludedPoolTypes, reservationAwareLedgerEntries],
    );

    const groupedEntries = useMemo(
        () => groupLedgerByLinkedOrBatch(filteredLedgerEntries),
        [filteredLedgerEntries],
    );

    const rawEntries = useMemo(
        () => ledgerEntriesNewestFirst(filteredLedgerEntries),
        [filteredLedgerEntries],
    );

    const togglePoolType = (poolType: string) => {
        setExcludedPoolTypes((current) => {
            const next = new Set(current);
            if (next.has(poolType)) {
                next.delete(poolType);
            } else {
                next.add(poolType);
            }
            return next;
        });
    };

    const toggleGroup = (groupKey: string) => {
        setExpandedGroupKeys((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
    };

    const handleCancelReservation = (entry: LedgerEntrySummary) => {
        const reservationId = entry.creditReservationId?.trim();
        if (!reservationId) {
            return;
        }
        setReservationError(null);
        setReservationConfirm({ kind: 'cancel', entry });
    };

    const cancelReservationConfirmed = async (entry: LedgerEntrySummary): Promise<boolean> => {
        const reservationId = entry.creditReservationId?.trim();
        if (!reservationId) {
            return false;
        }
        setCancellingReservationId(reservationId);
        try {
            await cancelCreditReservation.mutateAsync({
                reservationId,
                request: {
                    idempotencyKey: `admin-cancel-${reservationId}-${Date.now()}`,
                },
            });
        } catch (error) {
            console.error(error);
            setReservationError(
                error instanceof Error
                    ? error.message
                    : 'Could not cancel in-progress reservation. Try again.',
            );
            return false;
        } finally {
            setCancellingReservationId(null);
        }
        return true;
    };

    const handleCommitReservation = (entry: LedgerEntrySummary) => {
        const reservationId = entry.creditReservationId?.trim();
        if (!reservationId) {
            return;
        }
        setReservationError(null);
        setReservationConfirm({ kind: 'commit', entry });
    };

    const commitReservationConfirmed = async (entry: LedgerEntrySummary): Promise<boolean> => {
        const reservationId = entry.creditReservationId?.trim();
        if (!reservationId) {
            return false;
        }
        setCommittingReservationId(reservationId);
        try {
            await commitCreditReservation.mutateAsync({
                reservationId,
                request: {
                    idempotencyKey: `admin-commit-${reservationId}-${Date.now()}`,
                },
            });
        } catch (error) {
            console.error(error);
            setReservationError(
                error instanceof Error
                    ? error.message
                    : 'Could not commit in-progress reservation. Try again.',
            );
            return false;
        } finally {
            setCommittingReservationId(null);
        }
        return true;
    };

    const rowActionProps: LedgerRowActionProps = {
        ledgerEntries,
        onAdjustRow,
        onRefundRow,
        onReassignRow,
        onCommitReservation: handleCommitReservation,
        onCancelReservation: handleCancelReservation,
        committingReservationId,
        cancellingReservationId,
    };

    const renderRawRow = (entry: LedgerEntrySummary) => (
        <LedgerEntryRow
            key={entry.id}
            entry={entry}
            onShowDetails={setDetailsEntry}
            rowActionProps={rowActionProps}
        />
    );

    const renderGroup = ({
        groupKey,
        entries,
    }: {
        groupKey: string;
        entries: LedgerEntrySummary[];
    }) => {
        if (entries.length === 1) {
            return renderRawRow(entries[0]!);
        }

        const expanded = !!expandedGroupKeys[groupKey];
        const oldest = chronologicallyFirstLedgerEntry(entries);
        const compositeDescription = oldest?.description?.trim() ? oldest.description : '—';
        const poolTypesInGroup = [...new Set(entries.map((e) => e.poolType))];
        const latestTs = groupLatestTimestamp(entries);
        const groupAmountText = formatLedgerUsageAmount(
            entries.reduce((total, item) => total + getLedgerUsageAmount(item), 0),
        );
        const artifactSample = entries.find(
            (item) => item.linkedArtifactType?.trim() && item.linkedArtifactId?.trim(),
        );
        const representativeEntry = pickGroupedRowActionEntry(entries);
        const groupedUsageLabel = groupedLedgerUsageLabel(compositeLedgerState(entries));

        return (
            <Fragment key={groupKey}>
                <TableRow className="bg-neutral-50/90 hover:bg-neutral-50">
                    <TableCell className="w-10 !pr-1">
                        <button
                            type="button"
                            className="inline-flex h-8 min-w-9 items-center gap-1 rounded-md px-1 text-neutral-600 hover:bg-neutral-100 active:bg-neutral-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            aria-expanded={expanded}
                            aria-label={
                                expanded
                                    ? `Collapse grouped entries (${entries.length})`
                                    : `Expand grouped entries (${entries.length})`
                            }
                            onClick={(event) => {
                                event.stopPropagation();
                                toggleGroup(groupKey);
                            }}
                        >
                            {expanded ? (
                                <ChevronDown className="size-4 shrink-0" aria-hidden />
                            ) : (
                                <ChevronRight className="size-4 shrink-0" aria-hidden />
                            )}
                            <span
                                className="text-xs font-medium tabular-nums text-neutral-500"
                                aria-hidden
                            >
                                ({entries.length})
                            </span>
                        </button>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                        <span className="text-sm font-medium text-neutral-700">
                            {groupedUsageLabel}
                        </span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right font-semibold tabular-nums text-neutral-900">
                        {groupAmountText}
                    </TableCell>
                    <TableCell className="text-left">
                        <LinkedArtifactCell
                            artifactType={artifactSample?.linkedArtifactType}
                            artifactId={artifactSample?.linkedArtifactId}
                        />
                    </TableCell>
                    <TableCell>
                        <p
                            className="w-0 min-w-full line-clamp-2 break-words font-medium text-neutral-950"
                            title={compositeDescription}
                        >
                            {compositeDescription}
                        </p>
                    </TableCell>
                    <TableCell>
                        <div className="flex flex-wrap gap-1">
                            {poolTypesInGroup.map((poolType) => (
                                <span
                                    key={poolType}
                                    className={cn(
                                        adminCreditBadgeClassName,
                                        ledgerPoolBadgeClass(poolType),
                                    )}
                                >
                                    {getPoolLabel(poolType)}
                                </span>
                            ))}
                        </div>
                    </TableCell>
                    <TableCell>
                        <CreatedByUserCell
                            name={oldest?.createdByUserName}
                            email={oldest?.createdByUserEmail}
                        />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-neutral-700">
                        <LedgerTimestamp iso={latestTs} />
                    </TableCell>
                    <TableCell className="w-20">
                        <div className="flex items-center justify-end">
                            <LedgerRowActionsMenu entry={representativeEntry} {...rowActionProps} />
                        </div>
                    </TableCell>
                </TableRow>
                {expanded
                    ? ledgerEntriesNewestFirst(entries).map((entry, index, arr) => (
                          <LedgerEntryRow
                              key={entry.id}
                              entry={entry}
                              onShowDetails={setDetailsEntry}
                              rowActionProps={rowActionProps}
                              nested
                              isFirstInNestedBlock={index === 0}
                              isLastInNestedBlock={index === arr.length - 1}
                          />
                      ))
                    : null}
            </Fragment>
        );
    };

    const totalCount = viewMode === 'raw' ? rawEntries.length : groupedEntries.length;
    const sectionDefs: Array<{ key: 'pending' | 'committed'; label: string }> = [
        { key: 'pending', label: 'Reserved' },
        { key: 'committed', label: 'Spent' },
    ];

    return (
        <div className="space-y-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <SegmentedControl
                    label="Ledger view"
                    value={viewMode}
                    onChange={setViewMode}
                    options={[
                        { value: 'grouped', label: 'Grouped', icon: Layers },
                        { value: 'raw', label: 'Raw', icon: List },
                    ]}
                />
                {availablePoolTypes.length > 1 ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-neutral-600">Pool filter</span>
                        {availablePoolTypes.map((poolType) => {
                            const isIncluded = !excludedPoolTypes.has(poolType);
                            return (
                                <button
                                    key={poolType}
                                    type="button"
                                    onClick={() => togglePoolType(poolType)}
                                    aria-pressed={isIncluded}
                                    className={cn(
                                        adminCreditBadgeClassName,
                                        'min-h-6 gap-1 px-2.5 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                        ledgerPoolBadgeClass(poolType),
                                        !isIncluded && 'bg-white text-neutral-600 ring-neutral-300',
                                    )}
                                >
                                    {isIncluded ? (
                                        <Check className="size-3 shrink-0" aria-hidden />
                                    ) : null}
                                    {getPoolLabel(poolType)}
                                </button>
                            );
                        })}
                    </div>
                ) : null}
            </div>
            <Card className="overflow-clip">
                <TooltipProvider delayDuration={200}>
                    <Table
                        stickyHeader
                        aria-labelledby={labelledBy}
                        className="min-w-[62rem] w-full [&_td:first-child]:!pl-3 [&_td:nth-child(2)]:!pl-2 [&_th:first-child]:!pl-3 [&_th:nth-child(2)]:!pl-2 sm:[&_td:first-child]:!pl-3 sm:[&_th:first-child]:!pl-3"
                    >
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-10 !pr-1">
                                    <span className="sr-only">Expand</span>
                                </TableHead>
                                <TableHead className="w-[1%] whitespace-nowrap">Type</TableHead>
                                <TableHead className="w-[1%] whitespace-nowrap text-right">
                                    Amount
                                </TableHead>
                                <TableHead className="w-[1%] whitespace-nowrap">Linked</TableHead>
                                <TableHead className="w-full min-w-[12rem]">Description</TableHead>
                                <TableHead className="w-[1%] whitespace-nowrap">Pool</TableHead>
                                <TableHead className="min-w-[10rem]">User</TableHead>
                                <TableHead className="w-[1%] whitespace-nowrap">
                                    Timestamp
                                </TableHead>
                                <TableHead className="w-20">
                                    <span className="sr-only">Actions</span>
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {totalCount === 0 ? (
                                <LedgerEmptyRow
                                    colSpan={tableColSpan}
                                    hasLedgerEntries={ledgerEntries.length > 0}
                                    isLoading={isLoading}
                                    isError={isError}
                                    onRetry={onRetry}
                                    isRetrying={isRetrying}
                                />
                            ) : (
                                sectionDefs.map((section) => {
                                    if (viewMode === 'raw') {
                                        const rows = rawEntries.filter(
                                            (entry) => ledgerEntrySectionKey(entry) === section.key,
                                        );
                                        if (rows.length === 0) return null;
                                        return (
                                            <Fragment key={section.key}>
                                                <LedgerSectionHeaderRow
                                                    label={section.label}
                                                    count={rows.length}
                                                    colSpan={tableColSpan}
                                                />
                                                {rows.map(renderRawRow)}
                                            </Fragment>
                                        );
                                    }
                                    const groups = groupedEntries.filter(
                                        (group) =>
                                            (compositeLedgerState(group.entries) === 'pending') ===
                                            (section.key === 'pending'),
                                    );
                                    if (groups.length === 0) return null;
                                    return (
                                        <Fragment key={section.key}>
                                            <LedgerSectionHeaderRow
                                                label={section.label}
                                                count={groups.length}
                                                colSpan={tableColSpan}
                                            />
                                            {groups.map(renderGroup)}
                                        </Fragment>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TooltipProvider>
            </Card>
            <LedgerEntryDetailsDialog entry={detailsEntry} onClose={() => setDetailsEntry(null)} />
            <ConfirmDialog
                open={reservationConfirm !== null}
                title={
                    reservationConfirm?.kind === 'commit'
                        ? 'Commit reservation?'
                        : 'Cancel reservation?'
                }
                description={
                    reservationConfirm?.kind === 'commit'
                        ? "This in-progress reservation's usage will be recorded as done."
                        : 'This in-progress reservation will be cancelled and its hold released.'
                }
                confirmLabel={
                    reservationConfirm?.kind === 'commit'
                        ? 'Commit reservation'
                        : 'Cancel reservation'
                }
                pendingLabel={reservationConfirm?.kind === 'commit' ? 'Committing…' : 'Cancelling…'}
                destructive
                error={reservationError}
                onCancel={() => {
                    setReservationConfirm(null);
                    setReservationError(null);
                }}
                onConfirm={async () => {
                    const current = reservationConfirm;
                    if (!current) {
                        return;
                    }
                    const ok =
                        current.kind === 'commit'
                            ? await commitReservationConfirmed(current.entry)
                            : await cancelReservationConfirmed(current.entry);
                    if (ok) {
                        setReservationConfirm(null);
                    }
                }}
            />
        </div>
    );
}

function LedgerEmptyRow({
    colSpan,
    hasLedgerEntries,
    isLoading,
    isError,
    onRetry,
    isRetrying,
}: {
    colSpan: number;
    hasLedgerEntries: boolean;
    isLoading?: boolean;
    isError?: boolean;
    onRetry?: () => void;
    isRetrying?: boolean;
}) {
    return (
        <TableRow>
            <TableCell colSpan={colSpan} className="py-8 text-center text-neutral-500">
                {isLoading ? (
                    <LoadingState message="Loading ledger…" />
                ) : isError ? (
                    <InlineAlert
                        tone="error"
                        title="Couldn't load the ledger"
                        onRetry={onRetry}
                        isRetrying={isRetrying}
                        className="text-left"
                    />
                ) : hasLedgerEntries ? (
                    'No ledger items match the selected pool filters.'
                ) : (
                    'No ledger items found.'
                )}
            </TableCell>
        </TableRow>
    );
}

function LedgerSectionHeaderRow({
    label,
    count,
    colSpan,
}: {
    label: string;
    count: number;
    colSpan: number;
}) {
    return (
        <TableRow>
            <TableCell
                colSpan={colSpan}
                className={cn('py-2 !bg-neutral-100', overlineClassName, 'text-neutral-600')}
            >
                {label}
                <span className="ml-2 font-normal text-neutral-500">{count}</span>
            </TableCell>
        </TableRow>
    );
}

function LedgerCellPlaceholder({ align = 'center' }: { align?: 'center' | 'left' }) {
    return (
        <span
            className={cn(
                'block w-full text-sm text-neutral-400',
                align === 'left' ? 'text-left' : 'text-center',
            )}
        >
            —
        </span>
    );
}

/** Two-line timestamp; full value in tooltip on hover. */
function LedgerTimestamp({ iso }: { iso: string | number }) {
    const date = new Date(iso);
    const fullTimestamp = date.toLocaleString(undefined, {
        dateStyle: 'full',
        timeStyle: 'medium',
    });

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <div className="w-fit cursor-default leading-tight">
                    <div className="whitespace-nowrap text-sm text-neutral-700">
                        {date.toLocaleDateString()}
                    </div>
                    <div className="whitespace-nowrap text-xs text-neutral-500">
                        {date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    </div>
                </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
                {fullTimestamp}
            </TooltipContent>
        </Tooltip>
    );
}

/** Created-by user: display name with email underneath. */
function CreatedByUserCell({ name, email }: { name?: string | null; email?: string | null }) {
    const trimmedName = name?.trim();
    const trimmedEmail = email?.trim();
    if (!trimmedName && !trimmedEmail) {
        return <LedgerCellPlaceholder align="left" />;
    }
    return (
        <div className="min-w-0 max-w-[14rem] leading-tight">
            {trimmedName ? (
                <p className="break-words font-medium text-neutral-950" title={trimmedName}>
                    {trimmedName}
                </p>
            ) : null}
            {trimmedEmail ? (
                <p className="mt-0.5 break-words text-sm text-neutral-500" title={trimmedEmail}>
                    <BreakableEmail email={trimmedEmail} />
                </p>
            ) : null}
        </div>
    );
}

/** Linked artifact: prominent copyable ID with the artifact type as secondary text. */
function LinkedArtifactCell({
    artifactType,
    artifactId,
}: {
    artifactType?: string | null;
    artifactId?: string | null;
}) {
    const id = artifactId?.trim();
    const typeLabel = formatLinkedArtifactsTypeLabel(artifactType);
    if (!id) {
        return <LedgerCellPlaceholder align="left" />;
    }
    return (
        <div className="leading-tight">
            <div className="flex items-center gap-1.5">
                <span
                    className="max-w-[9rem] truncate text-sm font-medium tabular-nums text-neutral-900"
                    title={id}
                >
                    {id}
                </span>
                <CreditCopyButton value={id} label="Copy linked artifact ID" />
            </div>
            {typeLabel ? (
                <p className="mt-0.5 text-xs leading-4 text-neutral-500">{typeLabel}</p>
            ) : null}
        </div>
    );
}

type LedgerRowActionProps = {
    ledgerEntries: LedgerEntrySummary[];
    onAdjustRow: (entry: LedgerEntrySummary) => void;
    onRefundRow: (entry: LedgerEntrySummary) => void;
    onReassignRow: (entry: LedgerEntrySummary) => void;
    onCommitReservation: (entry: LedgerEntrySummary) => void;
    onCancelReservation: (entry: LedgerEntrySummary) => void;
    committingReservationId: string | null;
    cancellingReservationId: string | null;
};

function isLedgerMenuReady(
    open: boolean,
    menuPosition: { top: number; left: number } | null,
): boolean {
    return open && menuPosition !== null;
}

function LedgerEntryRow({
    entry,
    onShowDetails,
    rowActionProps,
    nested = false,
    isFirstInNestedBlock = false,
    isLastInNestedBlock = false,
}: {
    entry: LedgerEntrySummary;
    onShowDetails: (entry: LedgerEntrySummary) => void;
    rowActionProps: LedgerRowActionProps;
    nested?: boolean;
    isFirstInNestedBlock?: boolean;
    isLastInNestedBlock?: boolean;
}) {
    const amountText = formatLedgerUsageAmount(getLedgerUsageAmount(entry));

    return (
        <TableRow
            className={cn(
                nested && 'bg-primary-500/[0.07] hover:bg-primary-500/[0.1]',
                nested && isFirstInNestedBlock && 'border-t-2 border-t-neutral-300',
                nested && isLastInNestedBlock && 'border-b-2 border-b-neutral-300',
            )}
        >
            <TableCell className="w-10 !pr-1" />
            <TableCell
                className={cn(
                    'whitespace-nowrap',
                    nested && 'border-l-4 border-l-primary-500 pl-4',
                )}
            >
                <span className="text-sm font-medium text-neutral-700">
                    {getLedgerTypeDisplayLabel(entry)}
                </span>
            </TableCell>
            <TableCell className="whitespace-nowrap text-right font-medium tabular-nums text-neutral-900">
                {amountText}
            </TableCell>
            <TableCell className="text-left">
                <LinkedArtifactCell
                    artifactType={entry.linkedArtifactType}
                    artifactId={entry.linkedArtifactId}
                />
            </TableCell>
            <TableCell>
                {entry.description?.trim() ? (
                    <p
                        className="w-0 min-w-full line-clamp-2 break-words font-medium text-neutral-950"
                        title={entry.description}
                    >
                        {entry.description}
                    </p>
                ) : (
                    <LedgerCellPlaceholder />
                )}
            </TableCell>
            <TableCell className="whitespace-nowrap">
                <span
                    className={cn(adminCreditBadgeClassName, ledgerPoolBadgeClass(entry.poolType))}
                >
                    {getPoolLabel(entry.poolType)}
                </span>
            </TableCell>
            <TableCell>
                <CreatedByUserCell
                    name={entry.createdByUserName}
                    email={entry.createdByUserEmail}
                />
            </TableCell>
            <TableCell className="whitespace-nowrap">
                <LedgerTimestamp iso={entry.createdAt} />
            </TableCell>
            <TableCell className="w-20">
                <div className="flex items-center justify-end gap-2">
                    <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="size-8 shrink-0 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
                        aria-label="Details"
                        onClick={() => onShowDetails(entry)}
                    >
                        <Info className="size-4" aria-hidden />
                    </Button>
                    <div className="size-8 shrink-0">
                        <LedgerRowActionsMenu entry={entry} {...rowActionProps} />
                    </div>
                </div>
            </TableCell>
        </TableRow>
    );
}

const LEDGER_ROW_MENU_MIN_WIDTH_PX = 136;
const LEDGER_ROW_MENU_GAP_PX = 4;
const LEDGER_ROW_MENU_VIEWPORT_PADDING_PX = 8;

function computeLedgerRowMenuPosition(
    trigger: HTMLElement,
    menu: HTMLElement | null,
): { top: number; left: number } {
    const rect = trigger.getBoundingClientRect();
    const menuHeight = menu?.offsetHeight ?? 0;
    const menuWidth = Math.max(menu?.offsetWidth ?? 0, LEDGER_ROW_MENU_MIN_WIDTH_PX);
    const estimatedHeight = menuHeight > 0 ? menuHeight : 140;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward =
        spaceBelow < estimatedHeight + LEDGER_ROW_MENU_GAP_PX && spaceAbove > spaceBelow;
    const top = openUpward
        ? Math.max(
              LEDGER_ROW_MENU_VIEWPORT_PADDING_PX,
              rect.top - estimatedHeight - LEDGER_ROW_MENU_GAP_PX,
          )
        : rect.bottom + LEDGER_ROW_MENU_GAP_PX;
    const left = Math.max(
        LEDGER_ROW_MENU_VIEWPORT_PADDING_PX,
        Math.min(
            rect.right - menuWidth,
            window.innerWidth - menuWidth - LEDGER_ROW_MENU_VIEWPORT_PADDING_PX,
        ),
    );

    return { top, left };
}

function LedgerRowActionsMenu({
    entry,
    ledgerEntries,
    onAdjustRow,
    onRefundRow,
    onReassignRow,
    onCommitReservation,
    onCancelReservation,
    committingReservationId,
    cancellingReservationId,
}: LedgerRowActionProps & {
    entry: LedgerEntrySummary;
}) {
    const [open, setOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const showAdjust = canAdjustAdminLedgerRow(entry, ledgerEntries);
    const showRefund = canRefundAdminLedgerRow(entry, ledgerEntries);
    const showReassign = canReassignAdminLedgerRow(entry, ledgerEntries);
    const showCancel = canCancelPendingCreditReservation(entry);
    const showCommit = showCancel;
    const reservationId = entry.creditReservationId?.trim() ?? null;
    const isCancelling = reservationId !== null && cancellingReservationId === reservationId;
    const isCommitting = reservationId !== null && committingReservationId === reservationId;
    const { onMenuKeyDown, closeAndRestoreFocus } = useMenuKeyboard({
        open: isLedgerMenuReady(open, menuPosition),
        menuRef,
        triggerRef,
        onClose: () => setOpen(false),
    });

    useLayoutEffect(() => {
        if (!open) {
            setMenuPosition(null);
            return;
        }

        const updateMenuPosition = () => {
            const trigger = triggerRef.current;
            if (!trigger) {
                return;
            }
            setMenuPosition(computeLedgerRowMenuPosition(trigger, menuRef.current));
        };

        updateMenuPosition();
        const frame = requestAnimationFrame(updateMenuPosition);
        window.addEventListener('scroll', updateMenuPosition, true);
        window.addEventListener('resize', updateMenuPosition);

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('scroll', updateMenuPosition, true);
            window.removeEventListener('resize', updateMenuPosition);
        };
    }, [open, showAdjust, showCancel, showCommit, showRefund, showReassign]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) {
                return;
            }
            setOpen(false);
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [open]);

    if (!showAdjust && !showRefund && !showReassign && !showCommit && !showCancel) {
        return null;
    }

    const runAction = (action: () => void) => {
        closeAndRestoreFocus();
        action();
    };
    const groupA = showReassign || showCommit;
    const groupB = showAdjust || showRefund || showCancel;

    const menu = open ? (
        <div
            ref={menuRef}
            role="menu"
            aria-label="Row actions"
            onKeyDown={onMenuKeyDown}
            style={{
                position: 'fixed',
                top: menuPosition?.top ?? 0,
                left: menuPosition?.left ?? 0,
                visibility: menuPosition ? 'visible' : 'hidden',
                zIndex: 50,
            }}
            className={cn(popoverSurfaceClassName, 'min-w-[8.5rem] p-1 animate-menu-in')}
            onClick={(event) => event.stopPropagation()}
        >
            {showReassign ? (
                <button
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    className={menuItemClassName}
                    onClick={() => runAction(() => onReassignRow(entry))}
                >
                    Reassign…
                </button>
            ) : null}
            {showCommit ? (
                <button
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    className={menuItemClassName}
                    disabled={isCommitting || isCancelling}
                    onClick={() => runAction(() => onCommitReservation(entry))}
                >
                    {isCommitting ? 'Committing…' : 'Commit reservation'}
                </button>
            ) : null}
            {groupA && groupB ? <div role="separator" className={menuSeparatorClassName} /> : null}
            {showAdjust ? (
                <button
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    className={cn(menuItemClassName, menuItemDestructiveClassName)}
                    onClick={() => runAction(() => onAdjustRow(entry))}
                >
                    Adjust…
                </button>
            ) : null}
            {showRefund ? (
                <button
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    className={cn(menuItemClassName, menuItemDestructiveClassName)}
                    onClick={() => runAction(() => onRefundRow(entry))}
                >
                    Refund…
                </button>
            ) : null}
            {showCancel ? (
                <button
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    className={cn(menuItemClassName, menuItemDestructiveClassName)}
                    disabled={isCancelling || isCommitting}
                    onClick={() => runAction(() => onCancelReservation(entry))}
                >
                    {isCancelling ? 'Cancelling…' : 'Cancel reservation'}
                </button>
            ) : null}
        </div>
    ) : null;

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className="inline-flex size-8 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Row actions"
                aria-expanded={open}
                aria-haspopup="menu"
                onClick={(event) => {
                    event.stopPropagation();
                    setOpen((current) => !current);
                }}
            >
                <Ellipsis className="size-4" aria-hidden />
            </button>
            {menu ? createPortal(menu, document.body) : null}
        </>
    );
}
